'use client';

import { createContext, useRef, useContext } from 'react';
import { useStore } from 'zustand';
import { createGlobalStore } from '@/stores/global-store';
import * as GlobalStoreTypes from '@/stores/global-store';
import getCoupons from '@/actions/byMember/getCoupons';
import Coupons from '@/models/Coupons';

/**
 * @type {import('zustand').StoreApi<GlobalStoreTypes.GlobalStore> | null}
 */
export const GlobalContext = createContext(null);

/**
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object} props.member
 * @returns {JSX.Element}
 */
export const GlobalContextProvider = ({ children, ...props }) => {
  /**
   * @type {React.RefObject<GlobalStoreTypes.GlobalStore>}
   */
  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = createGlobalStore();
  }

  const { coupons, setCoupons } = useStore(storeRef.current);
  const loadingRef = useRef(false);

  const getCouponsList = async (page = 1, status = 'all') => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    try {
      const res = await getCoupons({
        page,
        per_page: 10,
        status,
      });

      setCoupons(new Coupons(res.data));

      return res;
    } catch (error) {
      console.error(error);
    } finally {
      loadingRef.current = false;
    }
  };

  const updateCoupons = async (page = 1, status = 'all') => {
    try {
      const res = await getCoupons({
        page,
        per_page: 10,
        status,
      });

      coupons.insertCoupons(res.data);
      setCoupons(coupons);

      return res;
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <GlobalContext.Provider
      value={{ store: storeRef.current, getCouponsList, updateCoupons, ...props }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

/**
 *
 * @template T
 * @param {(store: GlobalStoreTypes.GlobalStore) => T} selector - A function that selects the desired value from the GlobalStore.
 * @returns {T} - The selected value from the GlobalStore.
 */
export const useGlobalStore = (selector) => {
  const globalContext = useContext(GlobalContext);

  if (!globalContext) {
    throw new Error('useGlobalStore must be used within GlobalStoreProvider');
  }

  return useStore(globalContext.store, selector);
};

export const useGlobalContext = () => {
  const globalContext = useContext(GlobalContext);

  if (!globalContext) {
    throw new Error('useGlobalContext must be used within GlobalStoreProvider');
  }

  const contextWithoutStore = { ...globalContext };
  delete contextWithoutStore.store;

  return contextWithoutStore;
};
